import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Award, Play } from "lucide-react";
import { PanHeader } from "../../components/pan/PanHeader";
import { Pill, TextLink, Eyebrow, Rule, H2, Card, Gauge, Bar, BigStat, Tabs } from "../../components/pan/PanUI";
import { SiteFooter } from "../../components/site/SiteFooter";
import { HolidayBanner } from "../../components/site/HolidayBanner";
import { HeroGlobe } from "../../components/site/HeroGlobe";
import { Reveal, MaskedLine, FadeIn, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { scrollToId } from "../../lib/lenis";
import { DISTRICTS, CONTACT } from "../../data/content";
import { SOLUTION_DETAILS } from "../../data/solutions";
import { SERVICES } from "../../data/services";
import { INDUSTRY_DETAILS } from "../../data/industries";
import { CASE_STUDIES } from "../../data/caseStudies";
import { ARTICLES } from "../../data/articles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sol = (slug) => SOLUTION_DETAILS.find((s) => s.slug === slug);
const svc = (slug) => SERVICES.find((s) => s.slug === slug);

const SUBNAV = [
  ["Why NexSecure?", "pan-why"],
  ["Solutions", "pan-platforms"],
  ["Services", "pan-services"],
  ["Proven Work", "pan-success"],
  ["Engage with Us", "pan-next"],
];

const PLATFORMS = [
  { tab: "Security & Surveillance", title: "Integrated security, engineered as one shield.", text: "CCTV, access control, biometrics, video door phones, fire alarm and intrusion detection — designed from a site survey, installed to standard and supported for life across Kerala.", stats: [["14", "districts covered"], ["24/7", "priority support"]], cta: ["/solutions/security-surveillance", "Explore Security & Surveillance"], links: ["security-surveillance", "access-control"], services: ["cctv-solutions", "access-control-systems", "biometric-attendance", "video-door-phones", "fire-alarm-systems"] },
  { tab: "Network & Communication", title: "Networks that quietly carry everything.", text: "Enterprise switching, routing, Wi-Fi engineered by heat-map survey, certified structured cabling and EPABX/intercom systems — the backbone your business, voice and video run on.", stats: [["Wi-Fi 6/7", "survey-designed"], ["Cat6A & fibre", "Fluke-certified"]], cta: ["/solutions/network-infrastructure", "Explore Network Infrastructure"], links: ["network-infrastructure", "wifi-wireless", "communication"], services: ["networking-solutions", "structured-cabling", "epabx-intercom"] },
  { tab: "Software & Cloud", title: "Software built by the team that runs your infrastructure.", text: "Custom business software, integrations with Tally, ERP and payment gateways, cloud and digital infrastructure — built, connected and maintained by one accountable partner.", stats: [["1", "accountable partner"], ["Custom", "built to your workflow"]], cta: ["/solutions/cloud-digital-infrastructure", "Explore Software & Cloud"], links: ["cloud-digital-infrastructure", "it-infrastructure", "it-products-supply"], services: ["software-services", "consulting-design", "system-design", "integration"] },
  { tab: "Automation & Power", title: "Buildings that run themselves. Power that never blinks.", text: "Smart office and building automation, meeting rooms that respond instantly, and right-sized UPS, batteries and protection so critical systems stay on — planned, installed and maintained.", stats: [["1–200", "kVA UPS range"], ["AMC", "lifecycle support"]], cta: ["/solutions/automation", "Explore Automation"], links: ["smart-office", "automation", "power-continuity"], services: ["ups-power-solutions", "amc", "commissioning"] },
];

const NEEDS = [
  { tab: "Secure your premises", text: "Cameras, doors, attendance and fire safety — one design, one partner.", links: ["security-surveillance", "access-control"] },
  { tab: "Connect your network", text: "Switching, Wi-Fi, cabling and communication engineered for uptime.", links: ["network-infrastructure", "wifi-wireless", "communication"] },
  { tab: "Run your software", text: "Custom software, integrations and cloud infrastructure built for your workflow.", links: ["cloud-digital-infrastructure", "it-infrastructure"] },
  { tab: "Automate your building", text: "Smart office and automation that responds to how your teams work.", links: ["smart-office", "automation"] },
  { tab: "Keep the power on", text: "UPS, batteries and protection sized for your critical loads.", links: ["power-continuity", "it-products-supply"] },
  { tab: "Solutions by industry", text: "Offices, hospitals, hotels, campuses, factories and communities.", industries: true },
];

const ENGAGE = {
  Businesses: [
    { img: "/images/services/office-installations.jpg", title: "Book a Site Visit", text: "Our engineers assess your premises anywhere in Kerala — physical, wireless and infrastructure.", cta: ["/consultation", "Plan a site assessment"] },
    { img: "/images/services/software-services.jpg", title: "Request a Quote", text: "Tell us your requirement and get a documented price within 24 hours.", cta: ["/request-a-quote", "Get a price in 24 hours"] },
    { img: "/images/services/cctv-solutions.jpg", title: "Browse the Store", text: "CCTV, networking, access control, UPS and IT products — genuine, authorised, supported.", cta: ["/store", "Shop products"] },
    { img: "/images/services/amc.jpg", title: "Under Pressure? We're Here.", text: "Priority breakdown response and AMC for IT, network, CCTV, access control and power systems.", cta: ["/support", "Get support now"] },
  ],
  Specialists: [
    { img: "/images/services/networking-solutions.jpg", title: "Technology Ecosystem", text: "Requirements first — then the right manufacturers for performance, reliability and cost.", cta: ["/technology", "See our approach"] },
    { img: "/images/services/structured-cabling.jpg", title: "Downloads & Datasheets", text: "Brochures, datasheets and documentation for the systems we design and install.", cta: ["/downloads", "Open the library"] },
    { img: "/images/services/fire-alarm-systems.jpg", title: "Frequently Asked Questions", text: "Straight answers on surveys, quotes, warranties, AMC and support.", cta: ["/faq", "Read the FAQ"] },
    { img: "/images/services/biometric-attendance.jpg", title: "Insights from the Field", text: "Practical knowledge from real installations across Kerala.", cta: ["/insights", "Read insights"] },
  ],
  Partners: [
    { img: "/images/services/ups-power-solutions.jpg", title: "Brands We Deal With", text: "Authorised partner for leading security, network and infrastructure brands.", cta: ["/brands-we-deal-with", "See our brands"] },
    { img: "/images/services/epabx-intercom.jpg", title: "Careers at NexSecure", text: "Join a team passionate about engineering, security and making a real impact.", cta: ["/careers", "View open positions"] },
    { img: "/images/services/apartment-installations.jpg", title: "Our Team", text: "Meet the engineers and specialists behind every installation.", cta: ["/our-team", "Meet the team"] },
    { img: "/images/services/access-control-systems.jpg", title: "About NexSecure", text: "A technology infrastructure and integrated solutions company rooted in Kerala.", cta: ["/about", "Our story"] },
  ],
  Customers: [
    { img: "/images/services/video-door-phones.jpg", title: "Track Your Order", text: "Follow your store order from confirmation to delivery.", cta: ["/track-order", "Track an order"] },
    { img: "/images/services/cctv-projects-2.jpg", title: "Payment Receipts", text: "Download receipts for payments made to NexSecure.", cta: ["/receipts", "Get a receipt"] },
    { img: "/images/services/fire-alarm-projects-2.jpg", title: "Your Inquiries", text: "Check the status of quotes, consultations and support requests.", cta: ["/inquiries", "View inquiries"] },
    { img: "/images/services/amc.jpg", title: "Support Centre", text: "Guidance and 24/7 support from engineers who know your site.", cta: ["/support", "Open support"] },
  ],
};

const BRANDS = ["Hikvision", "Dahua", "eSSL", "ZKTeco", "TP-Link Omada", "Matrix", "Honeywell", "Ubiquiti", "CP Plus"];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 120]);
  return (
    <section className="relative min-h-[92svh] flex items-center overflow-hidden bg-nx-black" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src="/images/home/pan-hero.webp" alt="" className="absolute inset-0 w-full h-full object-cover object-[15%_center]" fetchPriority="high" data-testid="home-hero-photo" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/30" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-nx-black to-transparent" />
      </motion.div>
      <div className="relative nx-wrap w-full pt-28 pb-20">
        <div className="absolute left-[-8%] sm:left-[-4%] top-1/2 -translate-y-1/2 w-[110vw] max-w-[660px] aspect-square opacity-60 pointer-events-none" aria-hidden="true" data-testid="home-hero-visual">
          <div className="absolute inset-16 rounded-full bg-nx-orange/15 blur-3xl" />
          <HeroGlobe className="absolute inset-0" accent="orange" />
        </div>
        <div className="relative">
          <h1 className="font-outfit font-light text-4xl sm:text-6xl lg:text-[3.9rem] text-white leading-[1.05] tracking-[-0.02em] drop-shadow-[0_2px_24px_rgba(0,0,0,0.8)]" data-testid="home-hero-title">
            <MaskedLine delay={0.2} innerClassName="lg:whitespace-nowrap">Securing everything</MaskedLine>
            <MaskedLine delay={0.4} innerClassName="lg:whitespace-nowrap">your business runs on.</MaskedLine>
            <MaskedLine delay={0.6} innerClassName="lg:whitespace-nowrap">With you in control.</MaskedLine>
          </h1>
          <FadeIn delay={0.9}>
            <p className="mt-6 max-w-2xl text-lg sm:text-2xl text-white/90 font-light drop-shadow-[0_2px_16px_rgba(0,0,0,0.9)]" data-testid="home-hero-subheading">Software, security, networks, automation and IT — engineered as one system across Kerala.</p>
          </FadeIn>
          <FadeIn delay={1.05}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <Pill to="/solutions" testid="home-hero-cta-explore">Explore Solutions</Pill>
              <TextLink to="/contact" testid="home-hero-cta-experts">Talk to our experts</TextLink>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const SubNav = () => (
  <div className="sticky top-16 sm:top-[72px] z-30 bg-nx-black/95 backdrop-blur border-y border-white/10" data-testid="home-subnav">
    <div className="nx-wrap flex items-center justify-between gap-4 h-14 overflow-x-auto no-scrollbar">
      <div className="flex items-center gap-6 sm:gap-10 whitespace-nowrap">
        {SUBNAV.map(([label, id]) => (
          <button key={id} onClick={() => scrollToId(id)} className="relative shrink-0 whitespace-nowrap text-[13px] tracking-[0.14em] text-white/90 hover:text-nx-orange transition-colors py-4 after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[3px] after:bg-nx-orange after:scale-x-0 hover:after:scale-x-100 after:transition-transform" data-testid={`home-subnav-${id}`}>{label}</button>
        ))}
      </div>
      <Link to="/consultation" className="hidden sm:inline-flex shrink-0 rounded-full bg-nx-orange text-white text-sm font-medium px-5 py-2.5 hover:bg-nx-orangelight transition-colors" data-testid="home-subnav-cta">Book a Site Visit</Link>
    </div>
  </div>
);

const NewWorld = () => (
  <section className="relative bg-nx-black py-20 sm:py-28 overflow-hidden" data-testid="home-newworld">
    <div className="pan-hatch-lg absolute top-0 right-0 w-[46rem] h-[30rem] pointer-events-none" />
    <div className="relative nx-wrap">
      <Reveal><H2>A new <span className="text-nx-orange">connected world</span> is here</H2></Reveal>
      <div className="mt-16 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
        <Reveal>
          <Eyebrow>The good news</Eyebrow>
          <h3 className="mt-4 font-outfit font-light text-3xl sm:text-5xl text-white leading-[1.1]">Technology is transforming how Kerala does business</h3>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-10">
          <Gauge value={100} label="14 districts" sub="of Kerala served on-site — from our base in Ernakulam" testid="home-gauge-0" />
          <Gauge value={100} label="11 solution areas" sub="engineered to work as one integrated architecture" testid="home-gauge-1" />
        </div>
      </div>
      <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="flex flex-col gap-8 order-2 lg:order-1">
          <Bar value={100} label="Solutions designed as one integrated system" testid="home-bar-0" />
          <Bar value={100} label="Installations tested & commissioned before handover" testid="home-bar-1" />
          <Bar value={100} label="Sites documented and covered by AMC & support" testid="home-bar-2" />
        </div>
        <Reveal className="order-1 lg:order-2">
          <Eyebrow>The bad news</Eyebrow>
          <h3 className="mt-4 font-outfit font-light text-3xl sm:text-5xl text-white leading-[1.1]">Disconnected vendors and undocumented systems are slowing businesses down.</h3>
        </Reveal>
      </div>
    </div>
  </section>
);

const Why = () => (
  <section id="pan-why" className="bg-nx-black py-20 sm:py-28 border-t border-white/10" data-testid="home-why">
    <div className="nx-wrap">
      <Reveal>
        <Eyebrow>Why NexSecure</Eyebrow>
        <H2 className="mt-5 max-w-5xl">One integrated architecture lets you run security, network, communication and IT as one system. And keep it healthy for life.</H2>
        <Pill to="/about" className="mt-9" testid="home-why-cta">See our approach</Pill>
      </Reveal>
      <Stagger className="mt-14 grid md:grid-cols-3 gap-5">
        <StaggerItem><BigStat value="16" unit="services" label="across one lifecycle" text="Consult, survey, design, supply, install, integrate, commission, document, support and manage — with one accountable partner." testid="home-stat-0" /></StaggerItem>
        <StaggerItem><BigStat value="24" unit="/ 7" label="priority support" text="Preventive maintenance, breakdown response and health reports across IT, network, CCTV, access control, Wi-Fi and power." testid="home-stat-1" /></StaggerItem>
        <StaggerItem><BigStat value="100" unit="%" label="survey-led designs" text="Every proposal starts with a site assessment — never from stock to clear — so the design fits your requirement and budget." testid="home-stat-2" /></StaggerItem>
      </Stagger>
    </div>
  </section>
);

const Platforms = () => {
  const [i, setI] = useState(0);
  const p = PLATFORMS[i];
  return (
    <section id="pan-platforms" className="relative bg-nx-black py-20 sm:py-28 overflow-hidden" data-testid="home-platforms">
      <div className="pan-hatch-lg absolute top-24 right-0 w-[40rem] h-[28rem] pointer-events-none" />
      <div className="relative nx-wrap">
        <Reveal><Rule /><H2 className="mt-4">Introducing the Solutions,<br />engineered as one.</H2></Reveal>
        <Tabs items={PLATFORMS.map((x) => x.tab)} active={i} onChange={setI} className="mt-12" testid="home-platform-tab" />
        <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <h3 className="font-outfit font-light text-3xl sm:text-4xl text-white leading-tight">{p.title}</h3>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">{p.text}</p>
            <div className="mt-8 flex flex-wrap gap-12">
              {p.stats.map(([v, l]) => (
                <div key={l}><p className="font-outfit font-medium text-4xl sm:text-5xl text-white">{v}</p><p className="mt-1 text-xs tracking-[0.14em] uppercase text-slate-400">{l}</p></div>
              ))}
            </div>
            <Pill to={p.cta[0]} className="mt-9" testid="home-platform-cta">{p.cta[1]}</Pill>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">Solutions & services in this area</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {p.links.map((slug) => sol(slug)).filter(Boolean).map((s) => (
                <Link key={s.slug} to={`/solutions/${s.slug}`} className="group flex items-start gap-3 rounded-xl bg-gradient-to-br from-[#e9a23b] to-[#f39b2c] text-black p-4 hover:brightness-110 transition" data-testid={`home-platform-link-${s.slug}`}>
                  <Award size={22} className="shrink-0 mt-0.5" />
                  <span><span className="block font-semibold text-sm">{s.name}</span><span className="block text-xs text-black/70 mt-0.5 leading-snug">{s.tagline}</span></span>
                </Link>
              ))}
              {p.services.map((slug) => svc(slug)).filter(Boolean).map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group flex items-center justify-between gap-3 rounded-xl border border-white/15 bg-nx-black2 text-white p-4 hover:border-nx-orange transition-colors" data-testid={`home-platform-service-${s.slug}`}>
                  <span className="text-sm">{s.name}</span><ArrowUpRight size={16} className="text-slate-500 group-hover:text-nx-orange" />
                </Link>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const DYN = [
  ["16", "Lifecycle Services", "/images/services/software-services.jpg"],
  ["11", "Solution Areas", "/images/services/networking-solutions.jpg"],
  ["8", "Industries Served", "/images/services/office-installations.jpg"],
  ["14", "Kerala Districts", "/images/services/apartment-installations.jpg"],
  ["6", "Delivery Steps — Supply to AMC", "/images/services/structured-cabling.jpg"],
  ["24/7", "Support Coverage", "/images/services/amc.jpg"],
];

const ScaleBand = () => (
  <section className="bg-nx-black py-20 sm:py-28 border-t border-white/10" data-testid="home-scale">
    <div className="nx-wrap">
      <Reveal><H2>So you can run at speed and scale.</H2><p className="mt-4 text-sm text-slate-400">Across offices, hospitals, hotels, campuses, factories and communities.</p></Reveal>
      <Stagger className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {DYN.map(([v, l, img], i) => (
          <StaggerItem key={l}>
            <div className="relative overflow-hidden rounded-2xl border border-white/10 h-44 sm:h-52" data-testid={`home-scale-${i}`}>
              <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6">
                <p className="font-outfit font-medium text-4xl sm:text-5xl text-white">{v}</p>
                <p className="mt-1 text-sm text-slate-300">{l}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ServicesBand = () => (
  <section id="pan-services" className="relative bg-nx-black py-20 sm:py-28 border-t border-white/10 overflow-hidden" data-testid="home-services">
    <div className="pan-hatch-lg absolute bottom-0 left-0 w-[36rem] h-[24rem] rotate-180 pointer-events-none" />
    <div className="relative nx-wrap">
      <Reveal><H2>Engineered-in.<br />Support-ready.</H2></Reveal>
      <div className="mt-12 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <Eyebrow>Services</Eyebrow>
          <h3 className="mt-4 font-outfit font-light text-3xl sm:text-4xl text-white leading-tight">Managed Services & Annual Maintenance</h3>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">We don't disappear after installation. Preventive maintenance, priority breakdown response, health reports and lifecycle planning keep your infrastructure healthy — on schedule, not on luck.</p>
          <div className="mt-8 flex flex-wrap gap-12">
            <div><p className="font-outfit font-medium text-4xl sm:text-5xl text-white">24 / 7 / 365</p><p className="mt-1 text-xs tracking-[0.14em] uppercase text-slate-400">support coverage</p></div>
            <div><p className="font-outfit font-medium text-4xl sm:text-5xl text-white">14</p><p className="mt-1 text-xs tracking-[0.14em] uppercase text-slate-400">districts on-site</p></div>
          </div>
          <Pill to="/services/amc" className="mt-9" testid="home-services-cta">Explore AMC & Managed Services</Pill>
        </Reveal>
        <Stagger className="grid grid-cols-2 gap-4">
          {[["Preventive", "scheduled maintenance"], ["Priority", "breakdown response"], ["Health", "reports & lifecycle plans"], ["16", "services, one partner"]].map(([v, l]) => (
            <StaggerItem key={l}><Card className="p-6 h-full"><p className="font-outfit font-medium text-3xl sm:text-4xl text-white">{v}</p><p className="mt-2 text-sm text-slate-400">{l}</p></Card></StaggerItem>
          ))}
        </Stagger>
      </div>
      <Reveal className="mt-14">
        <div className="flex flex-wrap gap-2" data-testid="home-service-chips">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="rounded-full border border-white/15 text-slate-300 text-xs sm:text-sm px-4 py-2 hover:border-nx-orange hover:text-nx-orange transition-colors">{s.name}</Link>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Needs = () => {
  const [i, setI] = useState(0);
  const n = NEEDS[i];
  return (
    <section className="bg-nx-black py-20 sm:py-28 border-t border-white/10" data-testid="home-needs">
      <div className="nx-wrap">
        <Reveal><H2>Secure whatever, wherever —<br />with less complexity.</H2></Reveal>
        <Tabs items={NEEDS.map((x) => x.tab)} active={i} onChange={setI} className="mt-12" testid="home-needs-tab" />
        <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="mt-10">
          <p className="text-lg text-slate-300 max-w-2xl">{n.text}</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(n.industries ? INDUSTRY_DETAILS : n.links.map(sol).filter(Boolean)).map((x) => (
              <Link key={x.slug} to={n.industries ? `/industries/${x.slug}` : `/solutions/${x.slug}`} className="group rounded-2xl border border-white/15 bg-nx-black2 p-6 hover:border-nx-orange transition-colors" data-testid={`home-needs-link-${x.slug}`}>
                <span className="flex items-center justify-between"><span className="font-outfit text-lg text-white">{x.name}</span><ArrowRight size={16} className="text-slate-500 group-hover:text-nx-orange group-hover:translate-x-1 transition-all" /></span>
                <span className="block mt-3 text-sm text-slate-400 leading-relaxed">{x.tagline || x.intro}</span>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Customers = () => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const start = () => { setPlaying(true); requestAnimationFrame(() => videoRef.current?.play()); };
  return (
    <section id="pan-success" className="bg-nx-black py-20 sm:py-28 border-t border-white/10" data-testid="home-customers">
      <div className="nx-wrap">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <H2>Our customers are securing<br />their infrastructure</H2>
            <TextLink to="/projects" testid="home-customers-projects">See our work</TextLink>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black group" data-testid="company-video-frame">
            <video ref={videoRef} className="w-full h-full object-cover" poster="/videos/company-video-poster.jpg" controls={playing} preload="metadata" playsInline onEnded={() => setPlaying(false)} data-testid="company-video-player">
              <source src="/videos/nexsecure-company-video.mp4" type="video/mp4" />
            </video>
            {!playing && (
              <button onClick={start} className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/10 transition-colors" aria-label="Play company video" data-testid="company-video-play">
                <span className="flex items-center justify-center w-20 h-20 rounded-full bg-nx-orange text-white shadow-[0_0_60px_rgba(250,88,45,0.5)] transition-transform duration-300 group-hover:scale-110"><Play size={30} className="ml-1" fill="currentColor" /></span>
              </button>
            )}
          </div>
        </Reveal>
        <Stagger className="mt-12 grid md:grid-cols-3 gap-5">
          {CASE_STUDIES.map((c) => (
            <StaggerItem key={c.slug}>
              <Link to={`/projects/${c.slug}`} className="group block rounded-2xl overflow-hidden border border-white/10 bg-nx-black2 hover:border-nx-orange/60 transition-colors" data-testid={`home-project-${c.slug}`}>
                <img src={c.image} alt={c.title} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="p-6"><Eyebrow>{c.category}</Eyebrow><h3 className="mt-3 font-outfit text-lg text-white leading-snug">{c.title}</h3><p className="mt-2 text-sm text-slate-400">{c.location}</p></div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-20">
          <p className="text-center text-xs tracking-[0.28em] uppercase text-white mb-10">Brand partnerships we deliver with</p>
          <div className="flex flex-wrap justify-center gap-x-14 gap-y-6" data-testid="home-brands">
            {BRANDS.map((b) => <span key={b} className="font-outfit text-xl sm:text-2xl text-slate-300 tracking-wide">{b}</span>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Engage = () => {
  const keys = Object.keys(ENGAGE);
  const [i, setI] = useState(0);
  return (
    <section id="pan-next" className="bg-nx-black py-20 sm:py-28 border-t border-white/10" data-testid="home-engage">
      <div className="nx-wrap">
        <Reveal><H2>Here for you.<br />Here for what's next.</H2></Reveal>
        <Tabs items={keys} active={i} onChange={setI} className="mt-12" testid="home-engage-tab" />
        <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ENGAGE[keys[i]].map((c) => (
            <Link key={c.title} to={c.cta[0]} className="group rounded-2xl overflow-hidden border border-white/10 bg-nx-black2 hover:border-nx-orange/60 transition-colors flex flex-col" data-testid={`home-engage-${c.cta[0].slice(1)}`}>
              <img src={c.img} alt="" className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-outfit text-xl text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{c.text}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm text-white border-b border-white/60 self-start pb-0.5 group-hover:text-nx-orange group-hover:border-nx-orange transition-colors">{c.cta[1]} <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Perspectives = () => (
  <section className="bg-nx-black py-20 sm:py-28 border-t border-white/10" data-testid="home-insights">
    <div className="nx-wrap">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <H2>Staying ahead demands<br />perspectives you can trust.</H2>
          <TextLink to="/insights" testid="home-insights-all">View all</TextLink>
        </div>
      </Reveal>
      <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {ARTICLES.slice(0, 4).map((a) => (
          <StaggerItem key={a.slug}>
            <Link to={`/insights/${a.slug}`} className="group flex flex-col h-full rounded-2xl overflow-hidden border border-white/10 bg-nx-black2 hover:border-nx-orange/60 transition-colors" data-testid={`home-insight-${a.slug}`}>
              <div className="p-6 flex-1"><Eyebrow>Article</Eyebrow><h3 className="mt-3 font-outfit text-lg text-white leading-snug">{a.title}</h3><p className="mt-3 text-xs text-slate-500">{a.category} · {a.readTime}</p></div>
              {a.image && <img src={a.image} alt="" className="w-full h-40 object-cover" loading="lazy" />}
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "newsletter", name: "Newsletter subscriber", phone: "0000000000", email, company: "", message: "Subscribe to news, events and alerts" });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe — please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="bg-nx-black3 border-t border-white/10 py-14" data-testid="home-newsletter">
      <div className="nx-wrap grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <p className="font-outfit text-2xl sm:text-3xl text-white">Get the latest news, invites to events, and technology alerts</p>
          <p className="mt-2 text-sm text-slate-400">Enter your email now to subscribe!</p>
        </div>
        {done ? (
          <p className="text-nx-orange" data-testid="newsletter-success">Thank you — you're on the list.</p>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="flex-1 rounded-full bg-transparent border border-white/30 text-white placeholder:text-slate-500 px-6 py-3.5 text-sm focus:outline-none focus:border-nx-orange" data-testid="newsletter-email" />
            <Pill type="submit" testid="newsletter-submit" className={busy ? "opacity-60 pointer-events-none" : ""}>{busy ? "Sending…" : "Sign up"}</Pill>
          </form>
        )}
        <p className="lg:col-span-2 text-xs text-slate-500">By submitting this form, I understand my personal data will be processed in accordance with the NexSecure <Link to="/legal/privacy-policy" className="underline hover:text-nx-orange">Privacy Policy</Link>. Contact: {CONTACT.info}</p>
      </div>
    </section>
  );
};

export default function HomePan() {
  return (
    <div className="font-publicsans bg-nx-black text-slate-200 antialiased" data-testid="home-page">
      <PanHeader />
      <HolidayBanner />
      <Hero />
      <SubNav />
      <NewWorld />
      <Why />
      <Platforms />
      <ScaleBand />
      <ServicesBand />
      <Needs />
      <Customers />
      <Engage />
      <Perspectives />
      <Newsletter />
      <SiteFooter />
      <p className="sr-only" data-testid="home-district-list">{DISTRICTS.join(", ")}</p>
    </div>
  );
}
